import { USER_SEGMENT } from '@intigral/constant/common';
import {
    NX_FREE_OPERATORS,
    NX_NO_OP_OPERATOR,
    NX_NO_PAYMENT_OPERATORS,
    NX_TPAY_OPERATOR,
} from '@intigral/constant/config/EnvDefaults';
import { IUserFullObject } from '@intigral/types';
import { isBaseSubscription, isFreeTier } from './subscription';

export const isValidUserBundleOperator = (userOperator: string, allowedOperators: object) => {
    const userOperatorArr = userOperator?.split('_');
    const allOperators = allowedOperators ? Object.values(allowedOperators) : [];
    return userOperatorArr?.length >= 2 && allowedOperators
        ? allOperators.includes('STC_' + (userOperatorArr[1] ?? '')) || allOperators?.includes(userOperator)
        : false;
};

export const isM4M5Operator = (userOperator: string, m4m5Operator = 'M4M5') => {
    return userOperator?.toUpperCase()?.includes(m4m5Operator?.toUpperCase());
};

export const isTamayouzOperator = (userOperator: string, tamayouzOperator = 'TAMAYOUZ') => {
    return userOperator?.toUpperCase()?.includes(tamayouzOperator?.toUpperCase());
};

export const getUserCountryCode = (countryConfigs: object, operator: string) => {
    operator = operator.toLowerCase();
    if (countryConfigs) {
        const operatorCountry = Object.values(countryConfigs).filter(
            (country) =>
                country.paymentMethods
                    .map((op: { operator: string }) => op.operator.toLowerCase())
                    .indexOf(operator) !== -1
        );
        if (Array.isArray(operatorCountry) && operatorCountry.length && operatorCountry[0].countryCode) {
            return operatorCountry[0].countryCode;
        }
    }
    return '';
};

/**
 * This function can be used to determine the validity of a user's payment method.
 * @param userOperator - The user's operator
 * @returns {boolean} Returns true if the user's oeprator belongs to free user operator.
 */
export const isFreeOperator = (userOperator: string): boolean => {
    return getFreeOperators().includes(userOperator.toUpperCase());
};

/**
 * Get free operators configured for the application.
 * @returns {string[]} The list of operators
 */
export const getFreeOperators = (): string[] => {
    const freeOperators = (process.env['NX_FREE_OPERATORS'] || NX_FREE_OPERATORS).split(',');
    return freeOperators.map((op) => op.toUpperCase());
};

export const getNoPaymentOperators = (): string[] => {
    const noPaymentOperators = (process.env['NX_NO_PAYMENT_OPERATORS'] || NX_NO_PAYMENT_OPERATORS).split(',');
    return noPaymentOperators.map((op) => op.toUpperCase());
};

export const isDefaultOperator = (userOperator: string): boolean => {
    const defaultOperators = ['DEFAULT'];
    return defaultOperators.includes(userOperator.toUpperCase());
};

/**
 * This function can be used to determine if the user has selected a payment method.
 * @param {IUserFullObject} user - The user object
 * @returns {boolean} Returns true if the user has selected a payment method.
 */
export const isPaymentMethodSelected = (user: IUserFullObject): boolean => {
    return getNoPaymentOperators().includes(user.operator.toUpperCase()) === false;
};

/**
 * This function can be used to determine if the free tier user has selected a payment method and no addons.
 * @param {IUserFullObject} user - The user object
 * @returns {boolean} Returns true if the user has selected a payment method.
 */
export const isFreeTierNoAddonsWithPayment = (user: IUserFullObject): boolean => {
    if (isPaymentMethodSelected(user) && isFreeUserHasAddons(user)) {
        return true;
    }
    return false;
};

export const canChangePaymentMethod = (user: IUserFullObject): boolean => {
    if (!isPaymentMethodSelected(user) || isFreeTierNoAddonsWithPayment(user)) {
        return true;
    }
    return false;
};

export const isFreeUserHasAddons = (user: IUserFullObject): boolean => {
    if (isFreeTier(user)) {
        const hasAddons = user?.subscriptionStatus?.find((sub) => {
            return isBaseSubscription(sub.subscriptionGuid) === false;
        });

        return hasAddons ? false : true;
    }
    return false;
};

/**
 * This function can be used to determine if the user is a no_op operator.
 * @param {string} userOperator
 * @returns {boolean} Returns true if the user is a no_op operator.
 */
export const isNoOpOperator = (userOperator: string): boolean => {
    const noOPOperators = (process.env['NX_NO_OP_OPERATOR'] || NX_NO_OP_OPERATOR).split(',');
    return noOPOperators.includes((userOperator || '').toUpperCase());
};

/**
 * This function can be used to determine if the user is tpay operator.
 * @param {string} userOperator
 * @returns {boolean} Returns true if the user is a tpay operator.
 */
export const isTPayOperator = (userOperator: string): boolean => {
    const tpayOperators = (process.env['NX_TPAY_OPERATOR'] || NX_TPAY_OPERATOR).split(',');
    return tpayOperators.includes((userOperator || '').toUpperCase());
};

/**
 * Function to check should show unsub,add addons buttons based on segments and user detail
 * @param user user object
 * @param enableMigration boolean
 * @returns boolean true if eligible else false
 */
export const isDefaultUserStatus = (user: IUserFullObject, enableMigration: boolean): boolean => {
    // const defaultUser = isDefaultOperator(user?.operator);
    const isB2BUser = user.segment?.toLocaleUpperCase() === USER_SEGMENT.B2B;
    const isB2B2cUser = user.segment?.toLocaleUpperCase() === USER_SEGMENT.B2B2C;
    const isbundleUser = user?.bundle ?? false;

    if (
        ((enableMigration && !user.secondaryProduct) || !enableMigration) && // defaultUser ||
        (isbundleUser || isB2BUser || isB2B2cUser)
    ) {
        return true;
    }
    return false;
};

export const residentialValidation = (
    residential_number_validation: any,
    mobileNumber: string,
    countryCode: string
) => {
    if (!/^[0-9]+$/.test(mobileNumber)) {
        return false;
    }
    const trimmedMobile = mobileNumber.startsWith(countryCode || '966')
        ? mobileNumber.replace(countryCode, '')
        : mobileNumber;
    const validationRule = residential_number_validation?.validation_numbers;
    if (
        Array.isArray(validationRule) &&
        trimmedMobile.length > 0 &&
        validationRule.includes(Number(trimmedMobile[0]))
    ) {
        return true;
    }
    return false;
};
